import React, { useRef, useState } from 'react';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { uploadImage, resolveImage } from '../../lib/api';

/**
 * Image input that allows uploading a file OR pasting an external URL.
 * Stores the resulting URL (relative `/api/uploads/...` or external) via onChange.
 */
const ImageInput = ({ value, onChange, label = 'Image', height = 180 }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const res = await uploadImage(file);
      onChange(res.url);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const applyUrl = () => {
    if (urlDraft.trim()) {
      onChange(urlDraft.trim());
      setShowUrlInput(false);
      setUrlDraft('');
    }
  };

  return (
    <div>
      {label && (
        <label className="mb-1.5 block text-[12.5px] font-medium text-[#0B2545]">
          {label}
        </label>
      )}
      <div
        className="relative flex w-full items-center justify-center overflow-hidden rounded-md border-2 border-dashed border-[#E6DDC8] bg-[#FAF6EC]"
        style={{ height }}
      >
        {value ? (
          <>
            <img
              src={resolveImage(value)}
              alt="preview"
              className="h-full w-full object-cover"
            />
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-[#0B2545] shadow hover:bg-red-50 hover:text-red-600"
              aria-label="Remove image"
            >
              <X size={14} />
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 text-[#0B2545]/50">
            <ImageIcon size={28} />
            <span className="text-[12px]">No image selected</span>
          </div>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handleFile}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 rounded-md border border-[#E6DDC8] bg-white px-3 py-1.5 text-[12.5px] font-medium text-[#0B2545] hover:bg-[#F6F2EA] disabled:opacity-60"
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {uploading ? 'Uploading...' : 'Upload file'}
        </button>
        <button
          type="button"
          onClick={() => setShowUrlInput((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-md border border-[#E6DDC8] bg-white px-3 py-1.5 text-[12.5px] font-medium text-[#0B2545] hover:bg-[#F6F2EA]"
        >
          {showUrlInput ? 'Cancel' : 'Use URL'}
        </button>
      </div>
      {showUrlInput && (
        <div className="mt-2 flex items-center gap-2">
          <input
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            placeholder="https://..."
            className="flex-1 rounded-md border border-[#E6DDC8] bg-white px-3 py-1.5 text-[13px] outline-none focus:border-[#C9A86A]"
          />
          <button
            type="button"
            onClick={applyUrl}
            className="rounded-md bg-[#0B2545] px-3 py-1.5 text-[12.5px] font-medium text-white hover:bg-[#08203a]"
          >
            Apply
          </button>
        </div>
      )}
      {error && <div className="mt-1 text-[12px] text-red-600">{error}</div>}
    </div>
  );
};

export default ImageInput;
